'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ── Leaf Sequence — the 6 Names of the IRL Lens tutorial ─────────────────────
const LEAF_SEQUENCE = [
  {
    step: 1,
    name: 'Al-Qadir',
    ar: 'الْقَادِر',
    meaning: 'The All-Powerful',
    color: '#c9a84c',
    bgColor: 'rgba(201,168,76,0.06)',
    borderColor: 'rgba(201,168,76,0.25)',
    note: 'Recognition begins with acknowledging divine capacity — the power behind every observable process. Before the leaf, there is Al-Qadir. The leaf does not grow by its own power.',
    zahir: 'The leaf unfurling from the bud',
    batin: 'Al-Qadir\'s decree preceding every manifestation',
    fieldPrompt: 'Where do you see power operating right now — not force, but the quiet capacity behind growth?',
  },
  {
    step: 2,
    name: 'Al-Khaliq',
    ar: 'الْخَالِق',
    meaning: 'The Creator',
    color: '#c9a84c',
    bgColor: 'rgba(201,168,76,0.05)',
    borderColor: 'rgba(201,168,76,0.2)',
    note: 'From power comes creation — the origination of form from nothing. The leaf did not design itself. Al-Khaliq originates the blueprint before the first cell divides.',
    zahir: 'The cellular structure of the leaf',
    batin: 'The blueprint that precedes every created form',
    fieldPrompt: 'What are you witnessing being created right now? A seed germinating. A community forming. Al-Khaliq creates continuously.',
  },
  {
    step: 3,
    name: 'Al-Bari',
    ar: 'الْبَارِئ',
    meaning: 'The Evolver',
    color: '#2d7a47',
    bgColor: 'rgba(45,122,71,0.06)',
    borderColor: 'rgba(45,122,71,0.25)',
    note: 'The fashioning of distinct form — each leaf shaped precisely through evolutionary unfolding, no two identical. Al-Bari separates and distinguishes form from formlessness over time.',
    zahir: 'The unique shape of this particular leaf',
    batin: 'Ten thousand years of prescribed evolutionary unfolding',
    fieldPrompt: 'Where do you see prescribed unfolding — not random change but staged transformation? A caterpillar. A community evolving through stages.',
  },
  {
    step: 4,
    name: 'Al-Musawwir',
    ar: 'الْمُصَوِّر',
    meaning: 'The Fashioner of Forms',
    color: '#2d7a47',
    bgColor: 'rgba(45,122,71,0.05)',
    borderColor: 'rgba(45,122,71,0.2)',
    note: 'The imaging — the visible form given to the created thing. The color, the veins, the texture. Zahir made manifest through Al-Musawwir. "He forms you in the wombs however He wills." (3:6)',
    zahir: 'The color, veins, and texture of the leaf',
    batin: 'The divine imaging that gives each form its unique signature',
    fieldPrompt: 'What forms do you see being fashioned around you? The face of a child. The pattern of a community. Al-Musawwir is still fashioning.',
  },
  {
    step: 5,
    name: 'Ar-Razzaq',
    ar: 'الرَّزَّاق',
    meaning: 'The Ever-Provider',
    color: '#2d8a87',
    bgColor: 'rgba(45,138,135,0.06)',
    borderColor: 'rgba(45,138,135,0.25)',
    note: 'The leaf is a provision engine — photosynthesis as Ar-Razzaq\'s mechanism. Rizq flows through the chlorophyll. The leaf feeds the world. "There is no creature on earth whose provision is not guaranteed by Allah." (11:6)',
    zahir: 'Photosynthesis — light converted to sustenance',
    batin: 'Ar-Razzaq\'s guarantee flowing through every created mechanism',
    fieldPrompt: 'Where is provision flowing right now? A food pantry. A community garden. A masjid distribution. Ar-Razzaq uses human hands.',
  },
  {
    step: 6,
    name: 'An-Nur',
    ar: 'النُّور',
    meaning: 'The Light',
    color: '#c9a84c',
    bgColor: 'rgba(201,168,76,0.06)',
    borderColor: 'rgba(201,168,76,0.25)',
    note: 'The sequence closes with light — the leaf receives An-Nur, converting divine light into sustenance. The arc completes: Power → Creation → Evolution → Form → Provision → Light. الْحَمْدُ لِلَّه',
    zahir: 'Sunlight striking the leaf surface',
    batin: 'An-Nur as the source of all illumination — physical and spiritual',
    fieldPrompt: 'Where do you see light enabling life right now? The sun on a garden. Knowledge illuminating a community. An-Nur is the closing state of every recognition arc.',
  },
];

// ── Sunnat Timeline — how the Leaf Sequence unfolds in creation ───────────────
const SUNNAT_TIMELINE_STEPS = [
  { step: 1, name: 'Al-Qadir', ar: 'الْقَادِر', event: 'Divine decree precedes manifestation', color: '#c9a84c', connector: true },
  { step: 2, name: 'Al-Khaliq', ar: 'الْخَالِق', event: 'Blueprint encoded in seed DNA', color: '#c9a84c', connector: true },
  { step: 3, name: 'Al-Bari', ar: 'الْبَارِئ', event: 'Germination — prescribed unfolding begins', color: '#2d7a47', connector: true },
  { step: 4, name: 'Al-Musawwir', ar: 'الْمُصَوِّر', event: 'Leaf form fashioned — zahir made visible', color: '#2d7a47', connector: true },
  { step: 5, name: 'Ar-Razzaq', ar: 'الرَّزَّاق', event: 'Photosynthesis active — provision flowing', color: '#2d8a87', connector: true },
  { step: 6, name: 'An-Nur', ar: 'النُّور', event: 'Light received — arc complete · الْحَمْدُ لِلَّه', color: '#c9a84c', connector: false },
];

// ── Geometry helpers ──────────────────────────────────────────────────────────
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function ringSegmentPath(
  cx: number, cy: number,
  innerR: number, outerR: number,
  startAngle: number, endAngle: number,
) {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd   = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd   = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc   = endAngle - startAngle > 180 ? '1' : '0';
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    'Z',
  ].join(' ');
}

// ── Leaf Sequence Concentric Ring ─────────────────────────────────────────────
// 6 Names mapped to 3 concentric rings (pairs share a ring), each as arc segments
const CX = 180;
const CY = 180;
const RING_RADII = [44, 78, 112, 146]; // inner edges of rings 0-2 + outer edge of ring 2
const SEG_GAP = 4; // degrees gap between segments

interface LeafRingProps {
  activeStep: number;
  completedSteps: Set<number>;
  onSelect: (i: number) => void;
}

// Ring assignment: steps 0-1 on ring 0, steps 2-3 on ring 1, steps 4-5 on ring 2
const RING_ASSIGNMENTS = [0, 0, 1, 1, 2, 2];
// Within each ring, two halves: first name = top half, second = bottom half
const HALF_ANGLES: [number, number][] = [
  [-90 + SEG_GAP / 2, 90 - SEG_GAP / 2],   // top half
  [90 + SEG_GAP / 2, 270 - SEG_GAP / 2],   // bottom half
];
const HALF_INDEX = [0, 1, 0, 1, 0, 1]; // which half each step occupies

function LeafConcentricRing({ activeStep, completedSteps, onSelect }: LeafRingProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  // Decorative star points on outer edge
  const starPoints = Array.from({ length: 8 }, (_, i) => {
    const angle = i * 45 - 90;
    const p1 = polarToCartesian(CX, CY, 158, angle);
    const p2 = polarToCartesian(CX, CY, 152, angle + 22.5);
    const p3 = polarToCartesian(CX, CY, 158, angle + 45);
    return `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`;
  });

  return (
    <div className="flex flex-col items-center">
      <style>{`
        @keyframes leafPulse {
          0%,100% { filter: drop-shadow(0 0 4px rgba(45,122,71,0.3)); }
          50%      { filter: drop-shadow(0 0 12px rgba(45,122,71,0.8)); }
        }
        .leaf-active-ring { animation: leafPulse 2s ease-in-out infinite; }
      `}</style>

      <svg
        viewBox={`0 0 ${CX * 2} ${CY * 2}`}
        className="w-full max-w-[340px] mx-auto"
        style={{ filter: 'drop-shadow(0 0 24px rgba(45,122,71,0.08))' }}
        aria-label="Leaf Sequence concentric ring — click a segment to navigate"
      >
        {/* Outer decorative circles */}
        <circle cx={CX} cy={CY} r={160} fill="none" stroke="white" strokeWidth="0.4" opacity="0.12" />
        <circle cx={CX} cy={CY} r={155} fill="none" stroke="white" strokeWidth="0.25" opacity="0.08" />

        {/* Star points */}
        {starPoints.map((pts, i) => (
          <polygon key={i} points={pts} fill="none" stroke="white" strokeWidth="0.5" opacity="0.18" />
        ))}

        {/* Geometric grid lines */}
        {Array.from({ length: 6 }, (_, i) => {
          const angle = (i * 60 - 90) * Math.PI / 180;
          return (
            <line
              key={i}
              x1={CX} y1={CY}
              x2={CX + 160 * Math.cos(angle)}
              y2={CY + 160 * Math.sin(angle)}
              stroke="white" strokeWidth="0.3" opacity="0.06"
            />
          );
        })}

        {/* Ring separator circles */}
        {RING_RADII.map((r, i) => (
          <circle key={i} cx={CX} cy={CY} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="0.5" />
        ))}

        {/* Leaf Sequence arc segments */}
        {LEAF_SEQUENCE.map((item, i) => {
          const ring      = RING_ASSIGNMENTS[i];
          const halfIdx   = HALF_INDEX[i];
          const [sa, ea]  = HALF_ANGLES[halfIdx];
          const innerR    = RING_RADII[ring] + 3;
          const outerR    = RING_RADII[ring + 1] - 3;
          const isActive  = activeStep === i;
          const isDone    = completedSteps.has(i);
          const isHovered = hovered === i;

          const fillOpacity  = isActive ? 0.22 : isDone ? 0.12 : isHovered ? 0.1 : 0.04;
          const strokeOpacity = isActive ? 0.9 : isDone ? 0.55 : isHovered ? 0.5 : 0.18;

          // Mid-angle for label placement
          const midAngle = (sa + ea) / 2;
          const labelR   = (innerR + outerR) / 2;
          const labelPt  = polarToCartesian(CX, CY, labelR, midAngle);

          return (
            <g
              key={item.name}
              className={isActive ? 'leaf-active-ring' : ''}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(i)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <path
                d={ringSegmentPath(CX, CY, innerR, outerR, sa, ea)}
                fill={item.color}
                fillOpacity={fillOpacity}
                stroke={item.color}
                strokeOpacity={strokeOpacity}
                strokeWidth={isActive ? 1.2 : 0.6}
                style={{ transition: 'all 0.25s ease' }}
              />
              {/* Arabic label inside segment */}
              <text
                x={labelPt.x}
                y={labelPt.y + 3}
                textAnchor="middle"
                fill={item.color}
                fillOpacity={isActive ? 1 : isDone ? 0.7 : 0.4}
                fontSize={isActive ? 8 : 7}
                fontFamily="serif"
                style={{ pointerEvents: 'none', transition: 'all 0.25s' }}
              >
                {item.ar}
              </text>
              {/* Completion tick */}
              {isDone && (
                <text
                  x={labelPt.x + (halfIdx === 0 ? 14 : -14)}
                  y={labelPt.y + 3}
                  textAnchor="middle"
                  fill="#2d7a47"
                  fontSize={7}
                  style={{ pointerEvents: 'none' }}
                >
                  ✓
                </text>
              )}
            </g>
          );
        })}

        {/* Center core — shows active step info */}
        <circle
          cx={CX} cy={CY} r={RING_RADII[0] - 2}
          fill={`${LEAF_SEQUENCE[activeStep].color}12`}
          stroke={LEAF_SEQUENCE[activeStep].color}
          strokeOpacity={0.5}
          strokeWidth={1}
          style={{ transition: 'all 0.3s' }}
        />

        {/* Center: step number */}
        <text x={CX} y={CY - 10} textAnchor="middle" fill="white" fontSize={9} fontFamily="monospace" letterSpacing={2} opacity={0.5}>
          STEP
        </text>
        <text
          x={CX} y={CY + 6}
          textAnchor="middle"
          fill={LEAF_SEQUENCE[activeStep].color}
          fontSize={18}
          fontFamily="monospace"
          fontWeight="bold"
          style={{ transition: 'all 0.3s' }}
        >
          {activeStep + 1}
        </text>
        <text x={CX} y={CY + 20} textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize={6} fontFamily="monospace" letterSpacing={1}>
          OF 6
        </text>

        {/* Outer ring label — active name */}
        <text
          x={CX} y={CY + 34}
          textAnchor="middle"
          fill={LEAF_SEQUENCE[activeStep].color}
          fillOpacity={0.7}
          fontSize={6}
          fontFamily="monospace"
          letterSpacing={1.5}
          style={{ transition: 'all 0.3s' }}
        >
          {LEAF_SEQUENCE[activeStep].name.toUpperCase()}
        </text>

        {/* Progress dots on outer decorative ring */}
        {LEAF_SEQUENCE.map((item, i) => {
          const angle = (i * 60) - 90;
          const pt    = polarToCartesian(CX, CY, 153, angle);
          return (
            <circle
              key={i}
              cx={pt.x} cy={pt.y}
              r={activeStep === i ? 3.5 : 2}
              fill={activeStep === i ? item.color : completedSteps.has(i) ? `${item.color}80` : 'rgba(255,255,255,0.12)'}
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              onClick={() => onSelect(i)}
            />
          );
        })}

        {/* Ring labels on left edge */}
        {[
          { label: 'POWER · CREATION', r: (RING_RADII[0] + RING_RADII[1]) / 2, color: '#c9a84c' },
          { label: 'EVOLUTION · FORM', r: (RING_RADII[1] + RING_RADII[2]) / 2, color: '#2d7a47' },
          { label: 'PROVISION · LIGHT', r: (RING_RADII[2] + RING_RADII[3]) / 2, color: '#2d8a87' },
        ].map(({ label, r, color }) => {
          const pt = polarToCartesian(CX, CY, r, 180);
          return (
            <text
              key={label}
              x={pt.x - 4} y={pt.y + 2}
              textAnchor="end"
              fill={color}
              fillOpacity={0.45}
              fontSize={4.5}
              fontFamily="monospace"
              letterSpacing={0.8}
              style={{ pointerEvents: 'none' }}
            >
              {label}
            </text>
          );
        })}
      </svg>

      {/* Compact name legend below ring */}
      <div className="flex flex-wrap justify-center gap-1 mt-2 px-2">
        {LEAF_SEQUENCE.map((item, i) => (
          <button
            key={item.name}
            onClick={() => onSelect(i)}
            className="flex items-center gap-1 px-2 py-1 rounded transition-all duration-200"
            style={{
              background: activeStep === i ? `${item.color}18` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${activeStep === i ? item.color + '50' : 'rgba(44,74,62,0.2)'}`,
              cursor: 'pointer',
              minHeight: '32px',
            }}
          >
            <span style={{ fontFamily: 'serif', fontSize: 10, color: item.color, opacity: activeStep === i ? 1 : 0.5 }}>{item.ar}</span>
            <span className="font-mono" style={{ fontSize: '0.5rem', color: activeStep === i ? '#F5F0E8' : 'rgba(245,240,232,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.name}</span>
            {completedSteps.has(i) && <span style={{ fontSize: 8, color: '#2d7a47' }}>✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IRLLensHubPreview() {
  const [activeStep, setActiveStep] = useState(0);
  const [showZahirBatin, setShowZahirBatin] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [ringLoaded, setRingLoaded] = useState(false);

  const currentLeaf = LEAF_SEQUENCE[activeStep];

  // Simulate ring load (SVG geometry computed client-side)
  React.useEffect(() => {
    const t = setTimeout(() => setRingLoaded(true), 200);
    return () => clearTimeout(t);
  }, []);

  const handleStepComplete = () => {
    setCompletedSteps(prev => new Set([...prev, activeStep]));
    if (activeStep < LEAF_SEQUENCE.length - 1) {
      setActiveStep(prev => prev + 1);
      setShowZahirBatin(false);
    }
  };

  const handleSelect = (i: number) => {
    setActiveStep(i);
    setShowZahirBatin(false);
  };

  const allComplete = completedSteps.size === LEAF_SEQUENCE.length;

  return (
    <section
      id="irl-lens-preview"
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #060d12 0%, #0a1a12 50%, #060d12 100%)',
        paddingTop: 'clamp(56px, 8vw, 96px)',
        paddingBottom: 'clamp(56px, 8vw, 96px)',
        borderTop: '1px solid rgba(45,122,71,0.15)',
      }}
    >
      {/* Atmospheric glow */}
      <div
        className="absolute top-1/4 left-1/2 pointer-events-none"
        style={{
          width: 'clamp(200px,40vw,500px)',
          height: 'clamp(200px,40vw,500px)',
          background: 'radial-gradient(circle, rgba(45,122,71,0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
          transform: 'translateX(-50%)',
        }}
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div className="mb-3 flex flex-wrap gap-2">
          <span className="font-mono text-xs tracking-widest uppercase px-4 py-2 rounded" style={{ color: '#2d7a47', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.3)' }}>
            Inner Reality Lens · عَدَسَةُ الْوَاقِع
          </span>
          <span className="font-mono text-xs tracking-widest uppercase px-3 py-2 rounded" style={{ color: '#c9a84c', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            Leaf Sequence Tutorial
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start mb-10">
          <div>
            <h2 className="font-heading mb-4" style={{ fontSize: 'clamp(1.6rem, 3.8vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#F5F0E8', fontWeight: 600 }}>
              The IRL Lens —{' '}
              <span style={{ color: '#2d7a47', fontStyle: 'italic' }}>Go From Aware to Conscious</span>
            </h2>
            <p className="font-body leading-relaxed mb-4" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: 'rgba(245,240,232,0.6)', lineHeight: 1.8 }}>
              The Inner Reality Lens is a methodological tool for recognizing the Names and Attributes of Allah in observable creation. The Leaf Sequence is the primary tutorial — six Names visible in a single leaf, from divine power through provision to illumination.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {['Zahir · Batin', 'Sunnat Allah', 'Haqq · Amanah', 'Uboor'].map(tag => (
                <span key={tag} className="font-mono px-3 py-1 rounded" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Sunnat Timeline — adjacent panel */}
          <div className="rounded-card p-5 relative overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(201,168,76,0.4), rgba(45,122,71,0.3), rgba(45,138,135,0.3), rgba(201,168,76,0.4))' }} />
            <div className="flex items-center gap-2 mb-4">
              <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                ✦ Sunnat Timeline
              </span>
              <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.3)' }}>How the Leaf Sequence unfolds in creation</span>
            </div>
            <div className="relative">
              {/* Vertical connector */}
              <div className="absolute left-4 top-5 bottom-5 w-px hidden sm:block" style={{ background: 'linear-gradient(to bottom, rgba(201,168,76,0.3), rgba(45,122,71,0.2), rgba(45,138,135,0.2), rgba(201,168,76,0.3))' }} />
              <div className="flex flex-col gap-0">
                {SUNNAT_TIMELINE_STEPS.map((item, i) => (
                  <div key={item.step} className="flex gap-3 pb-3 last:pb-0 relative">
                    <button
                      onClick={() => handleSelect(i)}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center z-10 transition-all duration-200"
                      style={{
                        background: activeStep === i ? `${item.color}20` : 'rgba(6,13,18,0.8)',
                        border: `1px solid ${activeStep === i ? item.color : 'rgba(61,54,39,0.3)'}`,
                        borderRadius: '50%',
                        cursor: 'pointer',
                      }}
                    >
                      <span className="font-mono" style={{ fontSize: '0.6rem', color: activeStep === i ? item.color : 'rgba(201,168,76,0.3)' }}>{item.step}</span>
                    </button>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold uppercase tracking-wide" style={{ color: activeStep === i ? '#F5F0E8' : 'rgba(245,240,232,0.45)' }}>{item.name}</span>
                        <span style={{ fontFamily: 'serif', fontSize: 13, color: item.color, opacity: activeStep === i ? 1 : 0.5 }}>{item.ar}</span>
                        {completedSteps.has(i) && <span style={{ fontSize: 9, color: '#2d7a47' }}>✓</span>}
                      </div>
                      <p className="font-mono" style={{ fontSize: '0.6rem', color: activeStep === i ? 'rgba(245,240,232,0.6)' : 'rgba(245,240,232,0.25)', lineHeight: 1.5, marginTop: 2 }}>{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <span style={{ fontFamily: 'serif', fontSize: 14, color: '#c9a84c' }}>الْحَمْدُ لِلَّه</span>
              <span className="font-mono" style={{ fontSize: '0.55rem', color: 'rgba(201,168,76,0.5)' }}>— the closing state of every recognition arc</span>
            </div>
            <Link
              href="/names-reference?view=timelines"
              className="mt-3 flex items-center gap-2 font-mono transition-colors"
              style={{ fontSize: '0.6rem', color: 'rgba(201,168,76,0.5)', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.5)'; }}
            >
              Full Sunnat Timelines in Names Reference →
            </Link>
          </div>
        </div>

        {/* ── INTERACTIVE LEAF SEQUENCE WALKTHROUGH ───────────────────── */}
        {/* Row 1: Circular ring + detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">

          {/* ── CIRCULAR CONCENTRIC RING PANEL ── */}
          <div className="lg:col-span-1">
            <div
              className="rounded-card p-4 relative overflow-hidden"
              style={{
                background: 'rgba(6,13,18,0.85)',
                border: '1px solid rgba(45,122,71,0.2)',
              }}
            >
              {/* Top accent bar */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(45,122,71,0.5), rgba(201,168,76,0.3), rgba(45,138,135,0.3))' }} />

              <div className="flex items-center justify-between mb-3">
                <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Leaf Sequence · 6 Names
                </span>
                <span className="font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47', fontSize: '0.55rem' }}>
                  {completedSteps.size}/{LEAF_SEQUENCE.length} complete
                </span>
              </div>

              {/* Skeleton loader for ring while loading */}
              {!ringLoaded ? (
                <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', aspectRatio: '1', borderRadius: '50%', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 1.5s ease-in-out infinite' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,122,71,0.3)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Loading Ring...</span>
                </div>
              ) : (
                /* The circular ring visualization */
                <LeafConcentricRing
                  activeStep={activeStep}
                  completedSteps={completedSteps}
                  onSelect={handleSelect}
                />
              )}

              {/* Progress bar */}
              <div className="mt-3 p-3 rounded" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(45,122,71,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Recognition Progress</span>
                  <span className="font-mono" style={{ fontSize: '0.7rem', color: '#2d7a47', fontWeight: 'bold' }}>{completedSteps.size}/{LEAF_SEQUENCE.length}</span>
                </div>
                <div className="flex gap-1">
                  {LEAF_SEQUENCE.map((item, i) => (
                    <div
                      key={i}
                      className="flex-1 h-1 rounded-full transition-all duration-300"
                      style={{ background: completedSteps.has(i) ? item.color : activeStep === i ? `${item.color}40` : 'rgba(44,74,62,0.3)' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Active step detail */}
          <div className="lg:col-span-2">
            <div className="rounded-card p-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${currentLeaf.bgColor} 0%, rgba(6,13,18,0.95) 100%)`, border: `1px solid ${currentLeaf.borderColor}`, minHeight: 320 }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${currentLeaf.color}60, transparent)` }} />

              {/* Step header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Step {currentLeaf.step} of {LEAF_SEQUENCE.length}</span>
                    <span className="font-mono px-2 py-0.5 rounded" style={{ background: `${currentLeaf.color}15`, border: `1px solid ${currentLeaf.color}30`, color: currentLeaf.color, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Leaf Sequence
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span style={{ fontFamily: 'serif', fontSize: 32, color: currentLeaf.color, lineHeight: 1 }}>{currentLeaf.ar}</span>
                    <div>
                      <h3 className="font-mono font-bold uppercase tracking-wide" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: '#F5F0E8' }}>{currentLeaf.name}</h3>
                      <p className="font-mono" style={{ fontSize: '0.7rem', color: 'rgba(245,240,232,0.4)' }}>{currentLeaf.meaning}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main note */}
              <p className="font-body leading-relaxed mb-5" style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)', color: 'rgba(245,240,232,0.7)', lineHeight: 1.8 }}>
                {currentLeaf.note}
              </p>

              {/* Zahir / Batin toggle */}
              <div className="mb-4">
                <button
                  onClick={() => setShowZahirBatin(prev => !prev)}
                  className="font-mono text-xs px-4 py-2 rounded transition-all duration-200"
                  style={{ background: showZahirBatin ? 'rgba(90,90,138,0.15)' : 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.3)', color: '#8a8ab8', cursor: 'pointer', minHeight: '36px' }}
                >
                  {showZahirBatin ? '↑ Hide' : '↓ Reveal'} Zahir · Batin Axis
                </button>
                {showZahirBatin && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)' }}>
                      <p className="font-mono mb-1" style={{ fontSize: '0.6rem', color: '#2d7a47', textTransform: 'uppercase', letterSpacing: '0.1em' }}>ظَاهِر · Zahir (Visible)</p>
                      <p className="font-body" style={{ fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.6 }}>{currentLeaf.zahir}</p>
                    </div>
                    <div className="p-3 rounded" style={{ background: 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.2)' }}>
                      <p className="font-mono mb-1" style={{ fontSize: '0.6rem', color: '#8a8ab8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>بَاطِن · Batin (Hidden)</p>
                      <p className="font-body" style={{ fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.6 }}>{currentLeaf.batin}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Field prompt */}
              <div className="p-3 rounded mb-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(44,74,62,0.2)', borderLeft: `2px solid ${currentLeaf.color}40` }}>
                <p className="font-mono mb-1" style={{ fontSize: '0.55rem', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Field Observation Prompt</p>
                <p className="font-body italic" style={{ fontSize: '0.8rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>{currentLeaf.fieldPrompt}</p>
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-3">
                {activeStep > 0 && (
                  <button
                    onClick={() => { setActiveStep(prev => prev - 1); setShowZahirBatin(false); }}
                    className="font-mono text-xs px-4 py-2 rounded transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(44,74,62,0.3)', color: 'rgba(245,240,232,0.5)', cursor: 'pointer', minHeight: '40px' }}
                  >
                    ← Previous
                  </button>
                )}
                {activeStep < LEAF_SEQUENCE.length - 1 ? (
                  <button
                    onClick={handleStepComplete}
                    className="font-mono text-xs px-6 py-2 rounded transition-all duration-200 flex-1 sm:flex-none"
                    style={{ background: `${currentLeaf.color}20`, border: `1px solid ${currentLeaf.color}50`, color: currentLeaf.color, cursor: 'pointer', minHeight: '40px', fontWeight: 'bold' }}
                  >
                    Next Name →
                  </button>
                ) : (
                  <button
                    onClick={handleStepComplete}
                    className="font-mono text-xs px-6 py-2 rounded transition-all duration-200 flex-1 sm:flex-none"
                    style={{ background: 'rgba(201,168,76,0.2)', border: '1px solid rgba(201,168,76,0.5)', color: '#c9a84c', cursor: 'pointer', minHeight: '40px', fontWeight: 'bold' }}
                  >
                    الْحَمْدُ لِلَّه — Complete Arc
                  </button>
                )}
              </div>

              {/* Completion state */}
              {allComplete && (
                <div className="mt-4 p-4 rounded text-center" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)' }}>
                  <p style={{ fontFamily: 'serif', fontSize: 22, color: '#c9a84c', marginBottom: 4 }}>الْحَمْدُ لِلَّه</p>
                  <p className="font-mono" style={{ fontSize: '0.7rem', color: '#2d7a47', marginBottom: 8 }}>Recognition arc complete — all 6 Names witnessed in the leaf</p>
                  <Link
                    href="/inner-reality-lens"
                    className="inline-flex items-center gap-2 font-mono text-xs px-5 py-2 rounded transition-all duration-200"
                    style={{ background: 'rgba(45,122,71,0.2)', border: '1px solid rgba(45,122,71,0.5)', color: '#2d7a47', textDecoration: 'none', minHeight: '40px' }}
                  >
                    Access the Full IRL Lens →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── FOOTER CTA ───────────────────────────────────────────────── */}
        <div className="mt-10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" style={{ borderTop: '1px solid rgba(45,122,71,0.2)' }}>
          <div>
            <p className="font-heading mb-1" style={{ fontSize: 'clamp(1rem, 2.2vw, 1.3rem)', color: '#F5F0E8', fontWeight: 500 }}>
              The Full Lens — All 9 Cities, All Sequences
            </p>
            <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.45)', maxWidth: 480 }}>
              The full IRL Lens includes ward tile mosaics, ecological sequences, collaborative observation mode, and the complete Zahir · Batin depth panel across all nine cities.
            </p>
          </div>
          <Link
            href="/inner-reality-lens"
            className="group inline-flex items-center gap-3 font-body text-sm font-semibold px-7 py-4 rounded-btn transition-all duration-300 flex-shrink-0"
            style={{ background: 'rgba(45,122,71,0.2)', border: '1px solid rgba(45,122,71,0.5)', color: '#2d7a47', minHeight: '48px', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,122,71,0.35)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,122,71,0.2)'; }}
          >
            Access The Full IRL Lens
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  );
}
